import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Paper,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Import menu drawer
import { MenuDrawer } from "./MenuDrawer";

// Data imports
import insourced from "../Data/InSourced";
import outsourcedCosts from "../Data/OutSourced";
import transitionCosts from "../Data/transitionCosts";
import infrastructureSetupCosts from "../Data/infrastructureSetupCosts";

const CHART_COLORS = {
  "Total Cost": "rgba(230, 244, 255, 0.7)",
  "Operating Cost": "rgba(255, 240, 230, 0.7)",
  "One Time Costs": "rgba(230, 255, 236, 0.7)",
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    if (value === null) return null;

    return (
      <Paper sx={{ p: 2, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {payload[0].dataKey}: {formatCurrency(value)}
        </Typography>
      </Paper>
    );
  }
  return null;
};

const CostCard = ({ title, value, subtitle, bgcolor }) => (
  <Card sx={{ bgcolor, height: "100%" }}>
    <CardContent>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h5" component="div">
        {formatCurrency(value)}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const CostComparisonDashboard = () => {
  const [selectedView, setSelectedView] = useState("summary");
  const [menuOpen, setMenuOpen] = useState(false);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setSelectedView(newView);
    }
  };

  const insourcingData = [
    {
      name: 'Year 1',
      position: 1,
      'Total Cost': insourced.year1.yearTotal,
      'Operating Cost': null,
      'One Time Costs': null
    },
    {
      name: 'Year 1',
      position: 1.3,
      'Total Cost': null,
      'Operating Cost': insourced.year1.totalOperationalCosts,
      'One Time Costs': null
    },
    {
      name: 'Year 1',
      position: 1.6,
      'Total Cost': null,
      'Operating Cost': null,
      'One Time Costs': infrastructureSetupCosts.totalOneTimeCosts
    },
    {
      name: 'Year 2',
      position: 2,
      'Total Cost': insourced.year2.yearTotal,
      'Operating Cost': null,
      'One Time Costs': null
    },
    {
      name: 'Year 2',
      position: 2.3,
      'Total Cost': null,
      'Operating Cost': insourced.year2.totalOperationalCosts,
      'One Time Costs': null
    },
    {
      name: 'Year 2',
      position: 2.6,
      'Total Cost': null,
      'Operating Cost': null,
      'One Time Costs': 0
    },
    {
      name: 'Year 3',
      position: 3,
      'Total Cost': insourced.year3.yearTotal,
      'Operating Cost': null,
      'One Time Costs': null
    },
    {
      name: 'Year 3',
      position: 3.3,
      'Total Cost': null,
      'Operating Cost': insourced.year3.totalOperationalCosts,
      'One Time Costs': null
    },
    {
      name: 'Year 3',
      position: 3.6,
      'Total Cost': null,
      'Operating Cost': null,
      'One Time Costs': 0
    }
  ];

  const outsourcingData = [
    {
      name: 'Year 1',
      position: 1,
      'Total Cost': outsourcedCosts.year1.yearTotal,
      'Operating Cost': null,
      'One Time Costs': null
    },
    {
      name: 'Year 1',
      position: 1.3,
      'Total Cost': null,
      'Operating Cost': outsourcedCosts.year1.totalOperationalCosts,
      'One Time Costs': null
    },
    {
      name: 'Year 1',
      position: 1.6,
      'Total Cost': null,
      'Operating Cost': null,
      'One Time Costs': transitionCosts.totalOneTimeCosts
    },
    {
      name: 'Year 2',
      position: 2,
      'Total Cost': outsourcedCosts.year2.yearTotal,
      'Operating Cost': null,
      'One Time Costs': null
    },
    {
      name: 'Year 2',
      position: 2.3,
      'Total Cost': null,
      'Operating Cost': outsourcedCosts.year2.totalOperationalCosts,
      'One Time Costs': null
    },
    {
      name: 'Year 2',
      position: 2.6,
      'Total Cost': null,
      'Operating Cost': null,
      'One Time Costs': 0
    },
    {
      name: 'Year 3',
      position: 3,
      'Total Cost': outsourcedCosts.year3.yearTotal,
      'Operating Cost': null,
      'One Time Costs': null
    },
    {
      name: 'Year 3',
      position: 3.3,
      'Total Cost': null,
      'Operating Cost': outsourcedCosts.year3.totalOperationalCosts,
      'One Time Costs': null
    },
    {
      name: 'Year 3',
      position: 3.6,
      'Total Cost': null,
      'Operating Cost': null,
      'One Time Costs': 0
    }
  ];

  const renderSummaryView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Paper sx={{ p: 2, flex: 1, minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            Insourcing Cost Breakdown
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
            <LineChart data={insourcingData}>
  <XAxis 
    dataKey="name" 
    type="category"
    axisLine={true}
    tickLine={true}
  />
  <YAxis tickFormatter={(value) => `$${value / 1000000}M`} />
  <Tooltip content={<CustomTooltip />} />
  <Legend />
  <Line 
    type="monotone" 
    dataKey="Total Cost" 
    stroke="#8884d8" 
    strokeWidth={2}
    dot={{ r: 6 }}
    connectNulls
  />
  <Line 
    type="monotone" 
    dataKey="Operating Cost" 
    stroke="#82ca9d" 
    strokeWidth={2}
    dot={{ r: 6 }}
    connectNulls
  />
  <Line 
    type="monotone" 
    dataKey="One Time Costs" 
    stroke="#ffc658" 
    strokeWidth={2}
    dot={{ r: 6 }}
    connectNulls
  />
</LineChart>

            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, flex: 1, minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            Outsourcing Cost Breakdown
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={outsourcingData}>
                <XAxis dataKey="year" />
                <YAxis tickFormatter={(value) => `$${value / 1000000}M`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="Total Cost"
                  stroke="#8884d8"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="Operating Cost"
                  stroke="#82ca9d"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="One Time Costs"
                  stroke="#ffc658"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <CostCard
            title="Total Insourcing Investment (3 Years)"
            value={insourced.threeYearTotal}
            bgcolor={CHART_COLORS["Total Cost"]}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <CostCard
            title="Total Outsourcing Investment (3 Years)"
            value={outsourcedCosts.threeYearTotal}
            bgcolor={CHART_COLORS["Operating Cost"]}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderInsourcedView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Infrastructure Setup (One-Time Costs)
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {Object.entries(infrastructureSetupCosts).map(([key, value]) => {
            if (key !== "totalOneTimeCosts") {
              return (
                <Card
                  key={key}
                  sx={{
                    flex: "1 1 300px",
                    maxWidth: "calc(25% - 16px)",
                    minWidth: "250px",
                    bgcolor: CHART_COLORS["One Time Costs"],
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2">
                      {value.description}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {formatCurrency(value.cost)}
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            return null;
          })}
        </Box>
      </Paper>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {[insourced.year1, insourced.year2, insourced.year3].map(
          (yearData, index) => (
            <Paper key={index} sx={{ p: 3, flex: 1, minWidth: 300 }}>
              <Typography variant="h6" gutterBottom>
                Year {index + 1} Operational Costs
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                {formatCurrency(yearData.totalOperationalCosts)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {yearData.oneTimeInitiationCosts > 0 &&
                  `Plus ${formatCurrency(
                    yearData.oneTimeInitiationCosts
                  )} one-time costs`}
              </Typography>
            </Paper>
          )
        )}
      </Box>
    </Box>
  );

  const renderOutsourcedView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Transition Costs (One-Time)
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {Object.entries(transitionCosts).map(([key, value]) => {
            if (key !== "totalOneTimeCosts") {
              return (
                <Card
                  key={key}
                  sx={{
                    flex: "1 1 300px",
                    maxWidth: "calc(25% - 16px)",
                    minWidth: "250px",
                    bgcolor: CHART_COLORS["One Time Costs"],
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2">
                      {value.description}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {formatCurrency(value.cost)}
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            return null;
          })}
        </Box>
      </Paper>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {[
          outsourcedCosts.year1,
          outsourcedCosts.year2,
          outsourcedCosts.year3,
        ].map((yearData, index) => (
          <Paper key={index} sx={{ p: 3, flex: 1, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Year {index + 1} Operational Costs
            </Typography>
            <Typography variant="h4" color="primary" gutterBottom>
              {formatCurrency(yearData.totalOperationalCosts)}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
      }}
    >
      <AppBar position="static" sx={{ bgcolor: "white", color: "black" }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Cost Model Comparison
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, flexGrow: 1, overflow: "auto" }}>
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={selectedView}
            exclusive
            onChange={handleViewChange}
            sx={{ mb: 2 }}
            size="large"
          >
            <ToggleButton value="summary">Summary View</ToggleButton>
            <ToggleButton value="insourced">Insourced Details</ToggleButton>
            <ToggleButton value="outsourced">Outsourced Details</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {selectedView === "summary" && renderSummaryView()}
        {selectedView === "insourced" && renderInsourcedView()}
        {selectedView === "outsourced" && renderOutsourcedView()}
      </Box>

      <MenuDrawer
        menuOpen={menuOpen}
        handleMenuToggle={() => setMenuOpen(!menuOpen)}
        showStats={true}
        setShowStats={() => {}}
        showFilters={true}
        setShowFilters={() => {}}
        showControls={true}
        setShowControls={() => {}}
      />
    </Box>
  );
};

export default CostComparisonDashboard;
